import { useCallback, useEffect, useRef, useState } from 'react';
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes';
import { faCircleCheck } from '@fortawesome/pro-regular-svg-icons/faCircleCheck';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { FlexColumn } from 'ui-components/components/flex/column';
import { FlexRow } from 'ui-components/components/flex/row';
import { Text, TextType } from 'ui-components/components/text';
import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';
import { Gap } from 'ui-components/components/flex/gap';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import Global from '../../../../store/global-selectors';
import { fetchNotificationsRequest, markAllAsReadRequest } from '../../../../store/notification-hub/notification-hub.actions';
import { NotificationFilter } from '../../../../store/notification-hub/notification-hub.types';
import type { GroupedNotification } from '../../../../store/notification-hub/notification-hub.types';
import { NotificationFilters } from '../notification-filters';
import { GroupedNotificationItem } from '../grouped-notification-item';
import { NotificationFooter } from '../notification-footer';
import { NotificationEmptyState } from '../notification-empty-state';
import sharedStyle from '../../style.module.less';

import { messages } from './messages';
import style from './style.module.less';

type NotificationHubContentProps = {
  onToggleHub: () => void;
};

export const NotificationHubContent = ({ onToggleHub }: NotificationHubContentProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const dateGroupedNotifications = useSelector(Global.selectNotificationHubDateGroupedNotifications) ?? [];
  const activeFilter = useSelector(Global.selectNotificationHubActiveFilter);
  const [expandedNotificationId, setExpandedNotificationId] = useState<string | null>(null);
  const notificationsListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    dispatch(fetchNotificationsRequest());
  }, [dispatch]);

  useEffect(() => {
    if (notificationsListRef.current) {
      notificationsListRef.current.scrollTop = 0;
    }
    setExpandedNotificationId(null);
  }, [activeFilter]);

  const handleMarkAllAsRead = useCallback(() => {
    const priority = activeFilter === NotificationFilter.ALL || activeFilter === NotificationFilter.UNREAD ? undefined : activeFilter;
    dispatch(markAllAsReadRequest(priority));
  }, [dispatch, activeFilter]);

  const handleToggleExpand = useCallback((notificationId: string) => {
    setExpandedNotificationId((prev) => (prev === notificationId ? null : notificationId));
  }, []);

  return (
    <>
      <FlexRow className={sharedStyle.header}>
        <FlexRow className={sharedStyle['header-title']} gap={Gap.Space8}>
          <Text className={sharedStyle['header-label']} element='span' textType={TextType.H4} value={messages.notificationHubTitle} />
        </FlexRow>
        <FlexRow className={sharedStyle['header-actions']} gap={Gap.Space8}>
          <Tooltip>
            <Button ariaLabel={intl.formatMessage(messages.markAllAsRead)} onClick={handleMarkAllAsRead} theme={THEME_CONSTANTS.unstyled}>
              <FlexRow className={sharedStyle['toggle-hub']}>
                <FontAwesomeIcon icon={faCircleCheck} />
              </FlexRow>
            </Button>
            <TooltipContent contentClassName={style['nowrap-tooltip']}>{intl.formatMessage(messages.markAllAsRead)}</TooltipContent>
          </Tooltip>
          <Tooltip text={intl.formatMessage(messages.close)}>
            <Button ariaLabel={intl.formatMessage(messages.close)} onClick={onToggleHub} theme={THEME_CONSTANTS.unstyled}>
              <FlexRow className={sharedStyle['toggle-hub']}>
                <FontAwesomeIcon icon={faTimes} />
              </FlexRow>
            </Button>
          </Tooltip>
        </FlexRow>
      </FlexRow>

      <NotificationFilters />

      <FlexColumn ref={notificationsListRef} className={style['notifications-list']}>
        {dateGroupedNotifications.length === 0 ? (
          <NotificationEmptyState />
        ) : (
          <>
            {dateGroupedNotifications.map((dateGroup) =>
              dateGroup.notifications.map((groupedNotification: GroupedNotification) => (
                <GroupedNotificationItem
                  key={groupedNotification.notification.notificationID}
                  expandedNotificationId={expandedNotificationId}
                  groupedNotification={groupedNotification}
                  onToggleExpand={handleToggleExpand}
                />
              )),
            )}
            <NotificationFooter />
          </>
        )}
      </FlexColumn>
    </>
  );
};
